import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import {
  getQuestionnaires,
  getQuestionnaire,
  createQuestionnaire,
  updateQuestionnaire,
  deleteQuestionnaire,
  duplicateQuestionnaire,
  sendQuestionnaireToPatients,
  getPendingQuestionnaireLinks,
  getPatientQuestionnaireSubmissions,
  resendQuestionnaireSms,
} from "../../firebase";

export const questionnairesApi = createApi({
  reducerPath: "questionnairesApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Questionnaire", "QuestionnaireLink", "PatientQuestionnaireSubmission"],
  endpoints: (builder) => ({
    // ============================================
    // QUESTIONNAIRES (Reusable Templates - Like Forms)
    // ============================================

    // Get all questionnaires for clinic (visible to all therapists)
    getQuestionnaires: builder.query({
      async queryFn({ clinicId }) {
        try {
          const questionnaires = await getQuestionnaires(clinicId);
          return { data: questionnaires };
        } catch (error) {
          console.error("[CK-7100] Error fetching questionnaires:", {
            errorCode: "CK-7100",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Questionnaire", id })),
              { type: "Questionnaire", id: "LIST" },
            ]
          : [{ type: "Questionnaire", id: "LIST" }],
    }),

    // Get single questionnaire
    getQuestionnaire: builder.query({
      async queryFn({ clinicId, questionnaireId }) {
        try {
          const questionnaire = await getQuestionnaire(clinicId, questionnaireId);
          return { data: questionnaire };
        } catch (error) {
          console.error("[CK-7101] Error fetching questionnaire:", {
            errorCode: "CK-7101",
            errorMessage: error.message,
            clinicId,
            questionnaireId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { questionnaireId }) => [
        { type: "Questionnaire", id: questionnaireId },
      ],
    }),

    // Create questionnaire (reusable template)
    createQuestionnaire: builder.mutation({
      async queryFn({ clinicId, questionnaireData, creatorId, creatorName }) {
        try {
          const questionnaire = await createQuestionnaire(
            clinicId,
            questionnaireData,
            creatorId,
            creatorName
          );
          return { data: questionnaire };
        } catch (error) {
          console.error("[CK-7102] Error creating questionnaire:", {
            errorCode: "CK-7102",
            errorMessage: error.message,
            clinicId,
            questionnaireData,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Questionnaire", id: "LIST" }],
    }),

    // Update questionnaire
    updateQuestionnaire: builder.mutation({
      async queryFn({ clinicId, questionnaireId, questionnaireData, editorId, editorName }) {
        try {
          const questionnaire = await updateQuestionnaire(
            clinicId,
            questionnaireId,
            questionnaireData,
            editorId,
            editorName
          );
          return { data: questionnaire };
        } catch (error) {
          console.error("[CK-7103] Error updating questionnaire:", {
            errorCode: "CK-7103",
            errorMessage: error.message,
            clinicId,
            questionnaireId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { questionnaireId }) => [
        { type: "Questionnaire", id: questionnaireId },
        { type: "Questionnaire", id: "LIST" },
      ],
    }),

    // Delete questionnaire
    deleteQuestionnaire: builder.mutation({
      async queryFn({ clinicId, questionnaireId, questionnaireName, therapistId, therapistName }) {
        try {
          await deleteQuestionnaire(
            clinicId,
            questionnaireId,
            questionnaireName,
            therapistId,
            therapistName
          );
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7104] Error deleting questionnaire:", {
            errorCode: "CK-7104",
            errorMessage: error.message,
            clinicId,
            questionnaireId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Questionnaire", id: "LIST" }],
    }),

    // Duplicate questionnaire
    duplicateQuestionnaire: builder.mutation({
      async queryFn({ clinicId, questionnaireId, creatorId, creatorName }) {
        try {
          const questionnaire = await duplicateQuestionnaire(
            clinicId,
            questionnaireId,
            creatorId,
            creatorName
          );
          return { data: questionnaire };
        } catch (error) {
          console.error("[CK-7107] Error duplicating questionnaire:", {
            errorCode: "CK-7107",
            errorMessage: error.message,
            clinicId,
            questionnaireId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Questionnaire", id: "LIST" }],
    }),

    // Send questionnaire to patients (creates entries in patient files)
    sendQuestionnaireToPatients: builder.mutation({
      async queryFn({ clinicId, questionnaireId, patientIds }) {
        try {
          const result = await sendQuestionnaireToPatients(clinicId, questionnaireId, patientIds);
          trackFeature(FEATURE_KEYS.QUESTIONNAIRES);
          return { data: result };
        } catch (error) {
          console.error("[CK-7105] Error sending questionnaire:", {
            errorCode: "CK-7105",
            errorMessage: error.message,
            clinicId,
            questionnaireId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "QuestionnaireLink", id: "LIST" },
        { type: "PatientQuestionnaireSubmission", id: "LIST" },
      ],
    }),

    // Get pending questionnaire links (for "Waiting for Response" tab)
    getPendingQuestionnaireLinks: builder.query({
      async queryFn({ clinicId, therapistId }) {
        try {
          const links = await getPendingQuestionnaireLinks(clinicId, therapistId);
          return { data: links };
        } catch (error) {
          console.error("[CK-7106] Error fetching pending questionnaire links:", error.message);
          console.error("[CK-7106] Details:", { clinicId, therapistId, timestamp: new Date().toISOString() });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "QuestionnaireLink", id })),
              { type: "QuestionnaireLink", id: "LIST" },
            ]
          : [{ type: "QuestionnaireLink", id: "LIST" }],
    }),

    // Get questionnaire submissions for a patient (for patient profile)
    getPatientQuestionnaireSubmissions: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const submissions = await getPatientQuestionnaireSubmissions(clinicId, patientId);
          return { data: submissions };
        } catch (error) {
          console.error("[CK-7108] Error fetching patient questionnaire submissions:", {
            errorCode: "CK-7108",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "PatientQuestionnaireSubmission", id })),
              { type: "PatientQuestionnaireSubmission", id: `PATIENT_${patientId}` },
            ]
          : [{ type: "PatientQuestionnaireSubmission", id: `PATIENT_${patientId}` }],
    }),

    // Resend questionnaire SMS to patient
    resendQuestionnaire: builder.mutation({
      async queryFn({ clinicId, linkId }) {
        try {
          const result = await resendQuestionnaireSms(clinicId, linkId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7109] Error resending questionnaire:", {
            errorCode: "CK-7109",
            errorMessage: error.message,
            clinicId,
            linkId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [
        { type: "QuestionnaireLink", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetQuestionnairesQuery,
  useGetQuestionnaireQuery,
  useCreateQuestionnaireMutation,
  useUpdateQuestionnaireMutation,
  useDeleteQuestionnaireMutation,
  useDuplicateQuestionnaireMutation,
  useSendQuestionnaireToPatientsMutation,
  useGetPendingQuestionnaireLinksQuery,
  useGetPatientQuestionnaireSubmissionsQuery,
  useResendQuestionnaireMutation,
} = questionnairesApi;
